<template>
    <div class="files-list pt-4">
        <pui-collapsible
            :expandable="false"
            configurable
            headline="Files"
        >
            <template>
                <input
                    ref="inputRef"
                    hidden
                    type="file"
                    @change="onFileSelected"
                >
                <pui-button
                    slot="collapsibleConfigurationContent"
                    icon="add"
                    small
                    :disabled="isLoading"
                    @click="onAddFileClicked"
                >
                    Add File
                </pui-button>

                <pui-loader-spinner
                    v-if="isLoading"
                    title="Please wait!"
                />

                <template v-if="!isLoading">
                    <div 
                        v-for="file in fileList"
                        :key="file.id"
                        class="files-list__item"
                    >
                        <div class="files-list__item__title">
                            {{ file.name }}{{ file.extension }}
                        </div>
                        <div class="files-list__item__actions">
                            <pui-button
                                state="secondary"
                                icon="delete"
                                small
                                @click="onDeleteFileClicked(file.id)"
                            />
                            <pui-button
                                icon="download"
                                small
                                @click="onDownloadFileClicked(file.id, `${file.name}${file.extension}`)"
                            />
                        </div>
                    </div>
                </template>
            </template>
        </pui-collapsible>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { BudgetRequestsService } from '~/services/budget-request-service';

@Component({})
export default class FilesListComponent extends Vue {
    @Prop()
    private id?: string;

    @Prop()
    private globalId?: string;
    
    private budgetRequestService: BudgetRequestsService = new BudgetRequestsService();

    private isLoading = false;
    private fileList = [];

    async mounted() {
        await this.updateFileList()
    }

    private get isExistingBr(): boolean {
        return !!(this.id && this.globalId)
    }

    private async updateFileList(): Promise<void> {
        if (!this.isExistingBr) {
            return
        }

        this.isLoading = true
        
        try {
            const response = await this.budgetRequestService.getApprovalFileList(this.id)
            this.fileList = response.files
        } catch (e) {
            this.$pui.toast({
                type: 'error',
                title: 'Error!',
                copy: 'Could not get file list.',
            })
        } finally {
            this.isLoading = false
        }
    }

    private onAddFileClicked(): void {
        (this.$refs.inputRef as HTMLInputElement).click()
    }

    private async onFileSelected(event: Event): Promise<void> {
        const target = event.target as HTMLInputElement
        const file = target.files?.[0]

        if (!file) {
            return
        }

        if (this.isExistingBr) {
            await this.instantUploadFile(file)
            return
        }
        
        this.onSaveUploadFile(file)
    }

    private async instantUploadFile(file: File): Promise<void> {
        this.isLoading = true

        try {
            const response = await this.budgetRequestService.uploadApprovalFile(file, this.id, this.globalId)

            if (!response.isUploaded) {
                this.$pui.toast({
                    type: 'error',
                    title: 'Error!',
                    copy: response.error,
                })

                return
            } 
            
            await this.updateFileList()
        } catch (e) {
            this.$pui.toast({
                type: 'error',
                title: 'Error!',
                copy: 'Could not upload file.',
            })
        } finally {
            this.isLoading = false
        }
    }

    private onSaveUploadFile(file: File): void {

        this.fileList.push({
            id: Date.now(),
            name: file.name,
            fileContent: file,
        })

        this.$emit('fileListUpdated', this.fileList);
    }

    private async onDeleteFileClicked(id: string): Promise<void> {
        this.isLoading = true

        try {
            if (this.isExistingBr) {
                await this.budgetRequestService.deleteApprovalFile(id)
                await this.updateFileList()
            } else {
                this.fileList = this.fileList.filter(file => file.id !== id)
                this.$emit('fileListUpdated', this.fileList);
            }
        } catch (e) {
            this.$pui.toast({
                type: 'error',
                title: 'Error!',
                copy: 'Could not delete file.',
            })
        } finally {
            this.isLoading = false
        }
    }

    private async onDownloadFileClicked(id: string, name: string): Promise<void> {
        this.isLoading = true

        try {
            const response = await this.budgetRequestService.downloadApprovalFile(id)
            this.forceFileDownload(name, response)
        } catch (e) {
            this.$pui.toast({
                type: 'error',
                title: 'Error!',
                copy: 'Could not download file.',
            })
        } finally {
            this.isLoading = false
        }
    }

    private forceFileDownload(name, data) {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', name);
        document.body.appendChild(link);
        link.click();
    }

    @Watch('fileList')
    onFileListChanged() {
        const isTechnicalSpecificationProvided = this.fileList.length > 0
        this.$emit('update:isTechnicalSpecificationProvided', isTechnicalSpecificationProvided)
    }
}
</script>

<style lang="scss" scoped>
.files-list {
  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;

    min-height: 50px;
    padding: 0 16px;

    border-bottom: 1px solid #f9f9f9;

    &__actions {
      display: flex;
      justify-content: space-between;
    }
  }
}
</style>